import { useState, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import clsx from 'clsx';
import {
  Activity,
  CheckCircle2,
  FolderKanban,
  Gauge,
  Globe,
  Loader2,
  Plus,
  ShieldCheck,
  Smartphone,
  Sparkles,
  User,
  Users,
  XCircle,
  type IconComponent,
} from '@/components/icons';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { formatDuration, timeAgo } from '@/lib/format';
import type { DashboardCategory, DashboardCategoryBlock, DashboardView, SeverityCounts } from '@/lib/types';
import { Sparkline } from '@/components/charts/Sparkline';
import { RunsTable } from '@/components/report/RunsTable';
import { Card, CardHeader, EmptyState, ErrorNotice, LinkButton, PageHeader, PageLoader, StatusPill, Tabs } from '@/components/ui';

const CATEGORY_META: Record<DashboardCategory, { label: string; Icon: IconComponent; unit: string; rateLabel: string; empty: string }> = {
  web: { label: 'Web', Icon: Globe, unit: 'tests', rateLabel: 'Tests passed', empty: 'No web projects yet.' },
  mobile: { label: 'Mobile', Icon: Smartphone, unit: 'tests', rateLabel: 'Tests passed', empty: 'No Android or iOS projects yet.' },
  performance: { label: 'Performance', Icon: Gauge, unit: 'tests', rateLabel: 'Goals met', empty: 'No performance projects yet.' },
  security: { label: 'Security', Icon: ShieldCheck, unit: 'scans', rateLabel: 'Clean scans', empty: 'No security projects yet.' },
};

const SEVERITY_STYLE: { key: keyof SeverityCounts; label: string; className: string }[] = [
  { key: 'CRITICAL', label: 'Critical', className: 'bg-danger text-on-status' },
  { key: 'HIGH', label: 'High', className: 'bg-danger-subtle text-danger-text' },
  { key: 'MEDIUM', label: 'Medium', className: 'bg-warning-subtle text-fg' },
  { key: 'LOW', label: 'Low', className: 'bg-neutral-subtle text-fg-secondary' },
];

/** Figma UI kit, Stat Cards (node 1:1817): muted label + icon, big number with a smaller unit, caption. */
function Kpi({ label, value, unit, sub, Icon, tone = 'text-fg' }: { label: string; value: string; unit?: string; sub?: string; Icon: IconComponent; tone?: string }) {
  return (
    <Card className="flex flex-col gap-3 p-6">
      <div className="flex items-center justify-between">
        <span className="text-xs tracking-label text-fg-muted uppercase">{label}</span>
        <Icon className="size-3.5 text-fg-muted" />
      </div>
      <div className={clsx('truncate text-[44px]/[44px] font-semibold tracking-[-0.0385em] tabular-nums', tone)}>
        {value}
        {unit && <span className="ml-0.5 text-[24px]/6 tracking-[-0.07em] text-fg-secondary">{unit}</span>}
      </div>
      {sub && <div className="text-xs text-fg-secondary">{sub}</div>}
    </Card>
  );
}

/** Totals of the four categories for the KPI row (pass rate weighted by runs, as the categories count different things). */
function overview(view: DashboardView) {
  let runs = 0;
  let failedRuns = 0;
  let active = 0;
  let projects = 0;
  let rateSum = 0;
  let rateRuns = 0;
  for (const c of view.categories) {
    runs += c.stats.runs;
    failedRuns += c.stats.failedRuns;
    active += c.stats.active;
    projects += c.projects;
    if (c.stats.passRate !== null) {
      rateSum += c.stats.passRate * c.stats.runs;
      rateRuns += c.stats.runs;
    }
  }
  return { runs, failedRuns, active, projects, passRate: rateRuns ? Math.round(rateSum / rateRuns) : null };
}

function Figure({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div className="min-w-0">
      <div className="text-[11px] tracking-label text-fg-muted uppercase">{label}</div>
      <div className={clsx('mt-1 truncate text-xl font-semibold tabular-nums', tone ?? 'text-fg')}>{value}</div>
    </div>
  );
}

function CategoryCard({ block, mine, canCreate }: { block: DashboardCategoryBlock; mine: boolean; canCreate: boolean }) {
  const meta = CATEGORY_META[block.key];
  const s = block.stats;
  const hasTrend = s.trend.some((v) => v !== null);
  const empty = block.projects === 0;

  return (
    <Card className="flex flex-col" data-testid={`dashboard-category-${block.key}`}>
      <div className="flex items-start justify-between gap-3 p-5 pb-4">
        <div className="flex items-center gap-3">
          <span className="grid size-10 place-items-center rounded-lg bg-primary-subtle text-primary">
            <meta.Icon className="size-4" aria-hidden />
          </span>
          <div>
            <h2 className="text-base font-semibold text-fg">{meta.label}</h2>
            <p className="text-xs text-fg-secondary">
              {block.projects} {block.projects === 1 ? 'project' : 'projects'} · {block.tests} {mine ? `${meta.unit === 'scans' ? 'configs' : 'tests'} by you` : meta.unit === 'scans' ? 'scan configs' : 'tests'}
            </p>
          </div>
        </div>
        {s.active > 0 && (
          <span className="inline-flex items-center gap-1.5 rounded-full bg-pending-subtle px-2.5 py-1 text-xs text-fg">
            <Loader2 className="size-3 animate-spin" aria-hidden />
            {s.active} running
          </span>
        )}
      </div>

      {empty ? (
        <div className="flex flex-1 flex-col items-start justify-center gap-3 border-t border-border-subtle px-5 py-6">
          <p className="text-sm text-fg-muted">{mine ? `You have no ${meta.label.toLowerCase()} work yet.` : meta.empty}</p>
          {!mine && canCreate && (
            <LinkButton to="/projects?new=1" variant="secondary" size="sm" icon={<Plus className="size-3.5" />}>
              New project
            </LinkButton>
          )}
        </div>
      ) : (
        <>
          <div className="grid grid-cols-3 gap-4 border-t border-border-subtle px-5 py-4">
            <Figure label="Runs (7d)" value={String(s.runs)} />
            <Figure
              label={meta.rateLabel}
              value={s.passRate === null ? '—' : `${s.passRate}%`}
              tone={s.passRate === null ? undefined : s.passRate >= 90 ? 'text-success' : s.passRate >= 60 ? 'text-warning' : 'text-danger-text'}
            />
            <Figure label="Failed runs" value={String(s.failedRuns)} tone={s.failedRuns > 0 ? 'text-danger-text' : undefined} />
          </div>

          {block.key === 'performance' && s.verdicts && (
            <div className="flex flex-wrap gap-2 px-5 pb-4 text-xs">
              <Chip className="bg-success-subtle text-fg">Pass {s.verdicts.PASS}</Chip>
              <Chip className="bg-danger-subtle text-danger-text">Fail {s.verdicts.FAIL}</Chip>
              <Chip className="bg-warning-subtle text-fg">Degraded {s.verdicts.DEGRADED}</Chip>
              <Chip className="bg-neutral-subtle text-fg-secondary">Invalid {s.verdicts.INVALID}</Chip>
            </div>
          )}
          {block.key === 'security' && s.findings && (
            <div className="px-5 pb-4">
              <div className="mb-2 text-[11px] tracking-label text-fg-muted uppercase">
                Open findings · latest scan of {s.scannedProjects ?? 0} {s.scannedProjects === 1 ? 'project' : 'projects'}
              </div>
              <div className="flex flex-wrap gap-2 text-xs">
                {SEVERITY_STYLE.map((sev) => (
                  <Chip key={sev.key} className={s.findings![sev.key] ? sev.className : 'bg-surface-raised text-fg-muted'}>
                    {sev.label} {s.findings![sev.key]}
                  </Chip>
                ))}
              </div>
            </div>
          )}

          <div className="mt-auto border-t border-border-subtle px-5 py-4">
            {hasTrend ? (
              <>
                <Sparkline values={s.trend} percent height={48} width={320} label={`${meta.label}: daily ${meta.rateLabel.toLowerCase()} over the last 14 days`} />
                <div className="mt-1 flex justify-between text-[11px] text-fg-muted">
                  <span>14 days ago</span>
                  <span>Today</span>
                </div>
              </>
            ) : (
              <p className="py-3 text-center text-xs text-fg-muted">No finished runs in the last 14 days.</p>
            )}
          </div>

          <div className="flex items-center justify-between gap-3 border-t border-border-subtle px-5 py-3 text-xs text-fg-secondary">
            {s.lastRun ? (
              <Link to={`/runs/${s.lastRun.id}`} className="inline-flex items-center gap-2 hover:text-fg">
                Last run {timeAgo(s.lastRun.queuedAt)} <StatusPill status={s.lastRun.status} />
              </Link>
            ) : (
              <span>No runs yet</span>
            )}
            <span>Avg. {formatDuration(s.avgDurationMs)}</span>
          </div>
        </>
      )}
    </Card>
  );
}

function Chip({ className, children }: { className: string; children: ReactNode }) {
  return <span className={clsx('rounded-full px-2.5 py-1 font-medium tabular-nums', className)}>{children}</span>;
}

function RecentRuns({ view, mine }: { view: DashboardView; mine: boolean }) {
  const [tab, setTab] = useState<DashboardCategory>(() => view.categories.find((c) => view.recent[c.key].length)?.key ?? 'web');
  const runs = view.recent[tab] ?? [];
  return (
    <Card>
      <CardHeader
        title={mine ? 'Your recent runs' : 'Recent runs'}
        actions={
          <LinkButton to="/reports" variant="ghost" size="sm">
            View all
          </LinkButton>
        }
      />
      <Tabs
        label="Run category"
        value={tab}
        onChange={setTab}
        items={view.categories.map((c) => ({ id: c.key, label: CATEGORY_META[c.key].label, count: view.recent[c.key].length }))}
      />
      {runs.length === 0 ? (
        <div className="p-5">
          <EmptyState
            title={`No ${CATEGORY_META[tab].label.toLowerCase()} runs yet`}
            description={mine ? 'Runs you start show up here.' : 'Open a test project and press Run to see results here.'}
          />
        </div>
      ) : (
        <RunsTable runs={runs} />
      )}
    </Card>
  );
}

type ViewKey = 'all' | 'mine';

export function DashboardPage() {
  const { user, currentOrgId, can } = useAuth();
  const [params, setParams] = useSearchParams();
  const dashboard = useQuery({
    queryKey: ['dashboard', currentOrgId],
    queryFn: () => api.dashboard(currentOrgId!),
    enabled: !!currentOrgId,
    refetchInterval: 30_000,
  });

  const firstName = user?.name.split(' ')[0] ?? 'there';
  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';

  if (!currentOrgId || dashboard.isPending) return <PageLoader />;
  if (dashboard.isError) return <ErrorNotice error={dashboard.error} onRetry={() => dashboard.refetch()} />;

  const data = dashboard.data;
  const isAdmin = data.scope === 'all';
  const viewKey: ViewKey = isAdmin && params.get('view') === 'mine' && data.mine ? 'mine' : 'all';
  const view = viewKey === 'mine' && data.mine ? data.mine : data.team;
  const totals = overview(view);
  const teamProjects = overview(data.team).projects;
  const mine = viewKey === 'mine';

  const description = mine
    ? 'Runs you started and tests you created.'
    : isAdmin
      ? `Every project in your organization (${teamProjects}).`
      : `The ${teamProjects === 1 ? 'project' : `${teamProjects} projects`} you are assigned to.`;

  const setView = (next: ViewKey) => {
    const p = new URLSearchParams(params);
    if (next === 'mine') p.set('view', 'mine');
    else p.delete('view');
    setParams(p, { replace: true });
  };

  return (
    <div className="space-y-8">
      <PageHeader
        title={`${greeting}, ${firstName}`}
        description={description}
        actions={
          can('project.create') && (
            <LinkButton to="/projects?new=1" icon={<Plus className="size-4" />}>
              New test project
            </LinkButton>
          )
        }
      />

      {teamProjects === 0 ? (
        <Card className="relative overflow-hidden p-8">
          <div className="relative max-w-xl">
            <Sparkles className="size-8 text-primary" />
            {isAdmin ? (
              <>
                <h2 className="mt-4 text-2xl font-semibold tracking-[-0.04em] text-fg">Create your first automated test in minutes</h2>
                <p className="mt-2 text-fg-secondary">Tell 1QA where your app lives, then describe what a user does in plain English. No code required.</p>
              </>
            ) : (
              <>
                <h2 className="mt-4 text-2xl font-semibold tracking-[-0.04em] text-fg">You are not on a project yet</h2>
                <p className="mt-2 text-fg-secondary">A Project Admin adds you to projects. Their results show up here as soon as you are on one.</p>
              </>
            )}
            {can('project.create') && (
              <LinkButton to="/projects?new=1" size="lg" className="mt-6" icon={<FolderKanban className="size-5" />}>
                Set up a test project
              </LinkButton>
            )}
          </div>
        </Card>
      ) : (
        <>
          {isAdmin && (
            <div role="radiogroup" aria-label="Dashboard view" className="inline-flex rounded-field bg-surface-raised p-1 ring-1 ring-border ring-inset">
              {(
                [
                  { id: 'all', label: 'All projects', Icon: Users },
                  { id: 'mine', label: 'My work', Icon: User },
                ] as const
              ).map((o) => (
                <button
                  key={o.id}
                  type="button"
                  role="radio"
                  aria-checked={viewKey === o.id}
                  onClick={() => setView(o.id)}
                  data-testid={`dashboard-view-${o.id}`}
                  className={clsx(
                    'inline-flex items-center gap-2 rounded-md px-4 py-1.5 text-sm font-medium transition',
                    viewKey === o.id ? 'bg-surface text-fg shadow-sm ring-1 ring-border' : 'text-fg-muted hover:text-fg',
                  )}
                >
                  <o.Icon className="size-3.5" aria-hidden />
                  {o.label}
                </button>
              ))}
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <Kpi label="Runs this week" value={String(totals.runs)} sub={mine ? 'started by you' : 'all categories'} Icon={Activity} />
            <Kpi
              label="Pass rate (7 days)"
              value={totals.passRate === null ? '—' : String(totals.passRate)}
              unit={totals.passRate === null ? undefined : '%'}
              sub="weighted across categories"
              Icon={CheckCircle2}
              tone={totals.passRate === null ? 'text-fg' : 'text-success'}
            />
            <Kpi label="Failed runs" value={String(totals.failedRuns)} sub="last 7 days" Icon={XCircle} tone={totals.failedRuns > 0 ? 'text-primary' : 'text-fg'} />
            <Kpi label="Running now" value={String(totals.active)} sub="queued or in progress" Icon={Loader2} />
          </div>

          <section aria-label="By category" className="grid gap-6 md:grid-cols-2">
            {view.categories.map((block) => (
              <CategoryCard key={block.key} block={block} mine={mine} canCreate={can('project.create')} />
            ))}
          </section>

          <RecentRuns key={viewKey} view={view} mine={mine} />
        </>
      )}
    </div>
  );
}
